/**
 * ReleaseContext — global "active release" / baseline picker.
 *
 * When `activeRelease` is set, the UI is logically "viewing" the project at
 * that release's frozen file_manifest. Phase 1: state only — file fetches
 * do not yet pass `release_id`. Phase 2: file/version routers resolve
 * release_id → commit_id → file_manifest snapshot.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ReleaseContext = createContext({
  activeRelease: null,
  isLive: true,
  setActiveRelease: () => {},
  resetToLive: () => {},
});

export const ReleaseProvider = ({ children }) => {
  const [activeRelease, setActiveReleaseState] = useState(null);

  const setActiveRelease = useCallback((release) => {
    setActiveReleaseState(release || null);
  }, []);

  const resetToLive = useCallback(() => setActiveReleaseState(null), []);

  const value = useMemo(
    () => ({
      activeRelease,
      isLive: activeRelease === null,
      setActiveRelease,
      resetToLive,
    }),
    [activeRelease, setActiveRelease, resetToLive],
  );

  return <ReleaseContext.Provider value={value}>{children}</ReleaseContext.Provider>;
};

export const useRelease = () => useContext(ReleaseContext);

export default ReleaseContext;
